import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandPeace } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import T4Button from '../../common/T4Button';
import { register as registerApi, verifyAccount, resendVerificationCode, getMe } from '../../api';
import useUserStore from '../../hooks/useUserStore';
import './Register.css';

const Register = () => {
    const screenHeigth = window.innerHeight;
    const navbarHeight = 80;
    const location = useLocation();
    const navigate = useNavigate();
    const login = useUserStore((state) => state.login);

    const pendingEmail = location.state?.pendingVerificationEmail || '';

    const [step, setStep] = useState(pendingEmail ? 2 : 1);
    const [email, setEmail] = useState(pendingEmail);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [error, setError] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();

    const {
        register: registerStep2,
        handleSubmit: handleStep2,
        formState: { errors: errors2 },
    } = useForm();

    const onSubmit = async (data) => {
        setError(null);
        setLoading(true);
        try {
            await registerApi(data);
            setEmail(data.email);
            setResendCooldown(60);
            setStep(2);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al crear la cuenta', {
                position: toast.POSITION.TOP_RIGHT,
                className: 'md:w-auto max-w-full mx-3 my-3 md:mx-1',
                delay: 0,
                autoClose: 1000,
            });
        } finally {
            setLoading(false);
        }
    };

    const onVerifyCode = async (data) => {
        setError(null);
        setLoading(true);
        try {
            const res = await verifyAccount({ email, code: data.code });
            const token = res.data.token || res.data.data?.token;
            localStorage.setItem('token', token);
            const meRes = await getMe();
            const user = meRes.data.data?.name ? meRes.data.data : meRes.data.user || meRes.data;
            login(user, token);
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.message || 'Código de verificación incorrecto');
        } finally {
            setLoading(false);
        }
    };

    const onResendCode = async () => {
        setError(null);
        setResending(true);
        try {
            await resendVerificationCode(email);
            setResendCooldown(60);
            toast.success('Código reenviado a tu correo electrónico', {
                position: toast.POSITION.TOP_RIGHT,
                className: 'md:w-auto max-w-full mx-3 my-3 md:mx-1',
                delay: 0,
                autoClose: 3000,
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Error al reenviar el código');
        } finally {
            setResending(false);
        }
    };

    return (
        <div className='flex font-montserrat font-semibold w-full' style={{ height: `${screenHeigth - navbarHeight}px` }}>
            <div className='hidden md:flex md:max-w-[25%] lg:max-w-[50%] w-full login-image shadow-inner' />
            <div className='flex justify-center max-w-full md:max-w-[75%] lg:max-w-[50%] w-full mx-5'>
                <div className='bg-white shadow-xl w-full my-auto p-4 px-10 max-w-[500px] xl:max-w-[600px]'>
                    <img src='/logo.png' alt='' width='100px' className='mx-auto' />
                    <p className='text-center my-5 text-xl text-red-1'>
                        Bienvenido a Turkish4Love <FontAwesomeIcon icon={faHandPeace} />
                    </p>

                    {/* Step indicators */}
                    <div className='flex items-center gap-2 mb-5'>
                        <div className={`flex-1 h-1 rounded-full transition-colors ${step >= 1 ? 'bg-red-1' : 'bg-gray-200'}`} />
                        <div className={`flex-1 h-1 rounded-full transition-colors ${step >= 2 ? 'bg-red-1' : 'bg-gray-200'}`} />
                    </div>

                    {/* Step 1 — Registration form */}
                    {step === 1 && (
                        <>
                            <p className='text-center text-red-2 font-montserrat'>
                                Gracias por tu interés en ser parte de Turkish4love, para continuar debes crear tu cuenta.
                            </p>
                            <form className='flex flex-col' onSubmit={handleSubmit(onSubmit)}>
                                <label htmlFor='email' className='text-red-1 mt-5'>
                                    Correo electrónico
                                </label>
                                <input
                                    type='email'
                                    className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600'
                                    {...register('email', { required: true })}
                                />
                                {errors.email && <span className='text-sm text-red-600 mt-1'>Este campo es requerido</span>}

                                <label htmlFor='name' className='text-red-1 mt-5'>
                                    Nombre
                                </label>
                                <input
                                    className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600'
                                    {...register('name', { required: true })}
                                />
                                {errors.name && <span className='text-sm text-red-600 mt-1'>Este campo es requerido</span>}

                                <label htmlFor='lastname' className='text-red-1 mt-5'>
                                    Apellido
                                </label>
                                <input
                                    className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600'
                                    {...register('lastname', { required: true })}
                                />
                                {errors.lastname && <span className='text-sm text-red-600 mt-1'>Este campo es requerido</span>}

                                <label htmlFor='password' className='text-red-1 mt-5'>
                                    Contraseña
                                </label>
                                <input
                                    type='password'
                                    className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600'
                                    {...register('password', { required: true })}
                                />
                                {errors.password && <span className='text-sm text-red-600 mt-1'>Este campo es requerido</span>}

                                <div className='flex items-center mt-5'>
                                    <label htmlFor='removeMailing' className='text-red-1 mr-3'>
                                        <b className='text-darkred'>NO</b> quiero recibir avisos de nuevos capítulos
                                    </label>
                                    <input type='checkbox' {...register('removeMailing')} id='removeMailing' className='outline-none p-5' />
                                </div>

                                <button
                                    type='submit'
                                    disabled={loading}
                                    className='mt-6 bg-red-1 text-white font-semibold py-2 rounded hover:bg-red-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
                                >
                                    {loading ? 'Creando cuenta...' : 'Crear cuenta'}
                                </button>
                            </form>
                        </>
                    )}

                    {/* Step 2 — Verification code */}
                    {step === 2 && (
                        <form className='flex flex-col' onSubmit={handleStep2(onVerifyCode)}>
                            <p className='mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 text-center'>
                                Hemos enviado un código de verificación a tu correo electrónico.
                            </p>

                            <label htmlFor='code' className='text-red-1 mt-2'>
                                Código de verificación
                            </label>
                            <input
                                id='code'
                                className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600 tracking-widest'
                                type='text'
                                placeholder='Código de 6 dígitos'
                                maxLength={6}
                                {...registerStep2('code', { required: true })}
                            />
                            {errors2.code && <span className='text-sm text-red-600 mt-1'>Este campo es requerido</span>}

                            {error && (
                                <p className='mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 text-center'>
                                    {error}
                                </p>
                            )}

                            <button
                                type='submit'
                                disabled={loading}
                                className='mt-6 bg-red-1 text-white font-semibold py-2 rounded hover:bg-red-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
                            >
                                {loading ? 'Verificando...' : 'Verificar código'}
                            </button>
                            <hr className='my-3' />
                            <button
                                type='button'
                                onClick={onResendCode}
                                disabled={resending || resendCooldown > 0}
                                className='text-center text-sm text-red-1 hover:underline disabled:opacity-60 disabled:cursor-not-allowed'
                            >
                                {resending
                                    ? 'Reenviando...'
                                    : resendCooldown > 0
                                        ? `Reenviar código en ${resendCooldown}s`
                                        : '¿No recibiste el código? Reenviar'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Register;
